import React, { memo, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { CButton, CCol, CForm, CRow } from '@coreui/react';
import { Formik, ErrorMessage } from 'formik';
import { AIInsightsDetailContext } from '../Popup/AIInsightsTemplate';
import CFormGroup from '../../../../../migration/CFormGroup';
import CLabel from '../../../../../migration/CLabel';
import { CInputCheckbox } from '../../../../../migration/CInput';
import StepCardHeader from '../Components/StepCardHeaders'
import { toast } from 'react-toastify';
import { API_AI_INSIGHTS_TEMPLATES_BY_ACCOUNT_CREATE } from '../../../../../../constants';
import { callTokenApi } from '../../../../../../apiCaller';
import * as Yup from 'yup';
import { CInvalidFeedback } from '../../../../../migration/CInvalidFeedback';
import FormikStateObserver from '../Components/FormikStateObserver';
import isEqual from 'lodash/isEqual';
import Waiting from '../../../../../general/Loadings/Waiting';
import ToastEditor from '../../../../../general/toastEditor';

const FormikStateSyncAndDirtyReporter = memo(({
    stepId,
    currentFormValues,
    savedStepData,
    updateUnsavedEdits,
    handleSetUnsavedChanges
}) => {
    const previousValuesRef = useRef(currentFormValues);
    const stringifiedCurrentFormValues = useMemo(() => JSON.stringify(currentFormValues), [currentFormValues]);
    const stringifiedSavedStepData = useMemo(() => JSON.stringify(savedStepData), [savedStepData]);

    useEffect(() => {
        if (!isEqual(currentFormValues, previousValuesRef.current)) {
            updateUnsavedEdits(stepId, currentFormValues);
            previousValuesRef.current = currentFormValues;
        }

        const isDirtyVsSaved = !isEqual(currentFormValues, savedStepData);
        handleSetUnsavedChanges(isDirtyVsSaved);

    }, [
        stepId,
        stringifiedCurrentFormValues,
        stringifiedSavedStepData,
        updateUnsavedEdits,
        handleSetUnsavedChanges,
        currentFormValues,
        savedStepData
    ]);

    return null;
});

const ExampleOutput = () => {
    const {
        activeStep,
        setActiveStep,
        stepsData,
        setStepsData,
        unsavedEdits,
        updateUnsavedEdits,
        clearUnsavedEditsForStep,
        handleSetUnsavedChanges,
        clearUnsavedChangesFlag,
    } = useContext(AIInsightsDetailContext);

    const getRelevantStepFields = useCallback((sourceData) => {
        return {
            content: sourceData?.exampleOutputs?.trim() || sourceData?.content?.trim() || '',
            isExcludeExampleOutput: sourceData?.isExcludeExampleOutput || false,
            isIncludeReportReferencesFooter: sourceData?.isIncludeReportReferencesFooter || false,
            isIncludeReportReferencesInline: sourceData?.isIncludeReportReferencesInline || false,
        };
    }, []);

    const initialValues = useMemo(() => {
        const base = getRelevantStepFields(stepsData);
        const unsavedForThisStep = unsavedEdits[activeStep] || {};
        return { ...base, ...unsavedForThisStep };
    }, [stepsData, unsavedEdits, activeStep, getRelevantStepFields]);

    const savedStepData = useMemo(() => getRelevantStepFields(stepsData), [stepsData, getRelevantStepFields]);

    const validationSchema = Yup.object().shape({
        content: Yup.string().when(
            'isExcludeExampleOutput', // Check the exclude flag
            {
                is: (isExcluded) => isExcluded === false,
                then: (schema) => schema.required('Content is required when not excluded'),
                otherwise: (schema) => schema.nullable(),
            }
        ),
        isExcludeExampleOutput: Yup.boolean()
    });

    const onSubmit = useCallback(
        async values => {
            const { isExcludeExampleOutput, isIncludeReportReferencesFooter, isIncludeReportReferencesInline, content } = values;

            const currentStep = 4;

            const payload = {
                exampleOutputs: content?.trim() || '',
                isExcludeExampleOutput,
                isIncludeReportReferencesFooter,
                isIncludeReportReferencesInline,
                lastEditStep: stepsData.lastEditStep && stepsData.lastEditStep > currentStep
                    ? stepsData.lastEditStep
                    : currentStep,
            };

            try {
                const response = await callTokenApi(
                    `${API_AI_INSIGHTS_TEMPLATES_BY_ACCOUNT_CREATE}/${stepsData.id}`,
                    'PUT',
                    payload
                );
            
                if (response?.status === 200 || response?.data?.code === 200) {
                    setStepsData({
                        ...stepsData,
                        ...payload,
                    });
            
                    toast.success('Example output settings updated successfully.');
                    clearUnsavedEditsForStep(activeStep);
                    clearUnsavedChangesFlag();
                    setActiveStep(5);
                } else {
                    toast.error('Failed to save example output settings.');
                }
            } catch (error) {
                console.error('Submit error:', error);
                toast.error('An error occurred while saving. Please try again later.');
            }
        },
        [stepsData, setStepsData, setActiveStep, activeStep, clearUnsavedChangesFlag, clearUnsavedEditsForStep]
    );

    return (
        <StepCardHeader
            title={`Example Output`}
            step={4}
            activeStep={activeStep}
            lastEditStep={stepsData.lastEditStep}
            setActiveStep={() => setActiveStep(4)}
        >
            <p>Provide a tangible example of the output you'd like the insights analysis to generate.  This is possibly the most important portion of your instructions to the model. </p>

            <Formik
                initialValues={initialValues}
                onSubmit={onSubmit}
                enableReinitialize
                validateOnBlur={false}
                validationSchema={validationSchema}
            >
                {({ values, errors, touched, dirty, isSubmitting, handleSubmit, setFieldValue }) => {
                    return (
                        <>
                            <FormikStateObserver dirty={dirty} onDirtyChange={handleSetUnsavedChanges} />
                            <FormikStateSyncAndDirtyReporter
                                stepId={activeStep}
                                currentFormValues={getRelevantStepFields(values)}
                                savedStepData={savedStepData}
                                updateUnsavedEdits={updateUnsavedEdits}
                                handleSetUnsavedChanges={handleSetUnsavedChanges}
                            />
                            <CForm onSubmit={handleSubmit} noValidate>
                                <div className='d-flex align-items-center justify-content-between'>
                                    <CFormGroup variant="custom-checkbox" inline>
                                        <CInputCheckbox
                                            custom
                                            id='isExcludeExampleOutput'
                                            name='isExcludeExampleOutput'
                                            checked={!!values.isExcludeExampleOutput}
                                            className="m-auto"
                                            onChange={(e) => setFieldValue('isExcludeExampleOutput', e.target.checked)}
                                        />
                                        <CLabel htmlFor='isExcludeExampleOutput' variant="custom-checkbox">Do not include an example output (not recommended)</CLabel>
                                    </CFormGroup>

                                </div>

                                   <CRow className="my-4">
                                        <CCol lg="12">
                                             <CFormGroup>
                                                  <div className={`${errors.content && touched.content ? 'is-invalid' : ''}`}>
                                                       <ToastEditor
                                                            id="content.description"
                                                            initialValue={values.content}
                                                            disabledChange={!!values.isExcludeExampleOutput}
                                                            onChange={(content) => setFieldValue('content', content)}
                                                       />
                                                  </div>

                                            <CInvalidFeedback>
                                                <ErrorMessage name="content" />
                                            </CInvalidFeedback>
                                        </CFormGroup>
                                    </CCol>

                                    <CCol lg="12">
                                        <CFormGroup variant="custom-checkbox" inline>
                                            <CInputCheckbox
                                                custom
                                                id='isIncludeReportReferencesFooter'
                                                name='isIncludeReportReferencesFooter'
                                                checked={!!values.isIncludeReportReferencesFooter}
                                                className="m-auto"
                                                onChange={(e) => setFieldValue('isIncludeReportReferencesFooter', e.target.checked)}
                                            />
                                            <CLabel htmlFor='isIncludeReportReferencesFooter' variant="custom-checkbox">Include references to your reports at the bottom of the insights.</CLabel>
                                        </CFormGroup>
                                    </CCol>

                                </CRow>

                                <CButton disabled={isSubmitting} type="submit" className="px-4" color="primary">
                                    <Waiting isLoading={isSubmitting}>Save & Next</Waiting>
                                </CButton>
                            </CForm>
                        </>
                    );
                }}
            </Formik>
        </StepCardHeader>
    );
};

export default ExampleOutput;
